import {
  VerificationRespond,
  type ChangeEmail,
  type ChangePassword,
  type ChangePhone,
  type CheckVerification,
  type CurrentSessionUser,
  type Password,
  type Register,
  type ResetPassword,
  type UpdateMePost,
  type Verification,
} from '@entities/Authentication/types.ts';
import { clientsApi } from '@entities/clients/rtk-query.ts';
import { createApi } from '@reduxjs/toolkit/query/react';
import { getRtkConfig } from '@shared/config';
import { queryClient } from '@src/api/query-client.ts';

export const authApi = createApi({
  reducerPath: 'authentication',
  baseQuery: getRtkConfig('/api/v1/'),
  tagTypes: ['currentSessionUser'],
  keepUnusedDataFor: Number.MAX_SAFE_INTEGER,
  endpoints: (build) => ({
    getIAm: build.query<CurrentSessionUser, undefined>({
      query: (body) => ({
        url: `users/i_am/`,
        method: 'GET',
        body,
      }),
      providesTags: ['currentSessionUser'],
    }),
    deleteMe: build.mutation<undefined, void>({
      query: () => ({
        url: `users/user/delete_me/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['currentSessionUser'],
    }),
    updateMe: build.mutation<Partial<UpdateMePost>, Partial<UpdateMePost>>({
      query: ({ id, ...body }) => ({
        url: `users/user/${id}/updateme/`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        await authApi.util.invalidateTags(['currentSessionUser']);
        queryClient.clear();
        dispatch(clientsApi.util.resetApiState());
      },
      invalidatesTags: ['currentSessionUser'],
    }),
    changePassword: build.mutation<undefined, ChangePassword>({
      query: (body) => ({
        url: `auth/password/change/`,
        method: 'POST',
        body,
      }),
    }),
    verification: build.mutation<VerificationRespond, Verification>({
      query: (body) => ({
        url: `auth/request_verification/`,
        method: 'POST',
        body,
      }),
    }),
    checkVerification: build.mutation<CheckVerification, CheckVerification>({
      query: (body) => ({
        url: `auth/check_verification/`,
        method: 'POST',
        body,
      }),
    }),
    checkPassword: build.mutation<Password, Password>({
      query: (body) => ({
        url: `auth/check_password/`,
        method: 'POST',
        body,
      }),
    }),
    resetPassword: build.mutation<ResetPassword, ResetPassword>({
      query: (body) => ({
        url: `auth/reset_password/`,
        method: 'POST',
        body,
      }),
    }),
    register: build.mutation<{ token: string }, Register>({
      query: (body) => ({
        url: `auth/register/`,
        method: 'POST',
        body,
      }),
    }),
    changeEmail: build.mutation<ChangeEmail, ChangeEmail>({
      query: ({ userId, ...body }) => ({
        url: `users/user/${userId}/change_email/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['currentSessionUser'],
    }),
    changePhone: build.mutation<ChangePhone, ChangePhone>({
      query: ({ userId, ...body }) => ({
        url: `users/user/${userId}/change_phone/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['currentSessionUser'],
    }),
  }),
});

export const {
  useGetIAmQuery,
  useUpdateMeMutation,
  useChangePasswordMutation,
  useVerificationMutation,
  useCheckVerificationMutation,
  useCheckPasswordMutation,
  useRegisterMutation,
  useResetPasswordMutation,
  useChangeEmailMutation,
  useChangePhoneMutation,
  useDeleteMeMutation,
} = authApi;

export const authReducer = authApi.reducer;
export const authPath = authApi.reducerPath;
export const authMiddleware = authApi.middleware;
